import { inject, Injectable } from '@angular/core';
import { NetworkHelper, StatusHistoryActor } from '../helpers';
import { delay, Observable } from 'rxjs';
import { SHARED_ENV } from '../tokens';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

/**
 * `orderType` splits the mixed orders history: `fx` returns only FX rows, `order`
 * only everything else. `currency` matches either leg of the pair.
 */
export interface OrderHistoryFilters {
  orderType?: 'any' | 'order' | 'fx';
  currency?: string;
}

dayjs.extend(utc);

/**
 * An OTC estimate re-stated in the currency the client pays / receives. Equal
 * to the price-currency figures when the order is not paid in another currency.
 */
export interface OtcEstimatePayment {
  estimatedTotal: number;
  estimatedFee: number;
  estimatedAci?: number;
  overallTotal: number;
}

@Injectable({
  providedIn: 'root',
})
export class OrderService {
  private readonly env = inject(SHARED_ENV);
  private readonly hostUrl: string;

  constructor(private networkHelper: NetworkHelper) {
    this.hostUrl = this.env.host;
  }

  getTradingPairs(type: string, userId: string) {
    return this.networkHelper.get(`${this.hostUrl}/orders/trading-symbols`, {
      userId: userId,
      type: type,
    });
  }

  /**
   * The symbols this caller actually has trades for, for the symbol filter.
   *
   * Deliberately takes no `type`: a trade is terminal the moment it is written,
   * so there is nothing to be open. The backend param validator rejects
   * unknown keys outright, so passing one fails the whole call with
   * ERROR_VALIDATION and the dropdown comes back empty.
   *
   * Includes currency pairs (`USD/EUR`) from FX conversions, which is correct —
   * the lists this feeds do not filter FX out either. Omitting `userId` returns
   * every user's symbols for staff, but pass it explicitly: the orders endpoint
   * above behaves differently on omission, and relying on the difference is how
   * these three drift apart.
   */
  getTradesTradingSymbols(userId?: string): Observable<string[]> {
    return this.networkHelper.get(`${this.hostUrl}/trades/trading-symbols`, {
      userId: userId,
    });
  }

  /**
   * The symbols this caller actually has OTC orders for, for the symbol filter.
   *
   * No `type` and no status split, for the same reason the endpoint does not
   * offer one: `/otc-orders/history` shows pending, settled and cancelled on a
   * single screen, so narrowing the dropdown would hide options the list can
   * still display.
   */
  getOtcTradingSymbols(userId?: string): Observable<string[]> {
    return this.networkHelper.get(
      `${this.hostUrl}/otc-orders/trading-symbols`,
      { userId: userId }
    );
  }

  /**
   * Returns a bare array of stock, OTC and FX rows together — there is no
   * FX-specific listing endpoint.
   *
   * `page` / `itemsPerPage` are optional on this endpoint. Pass null for both when
   * the caller filters the rows itself (e.g. down to `extra.isFx`): filtering an
   * already-paginated page silently drops rows and breaks the pager.
   */
  getAllOpen(
    page: number | null,
    itemsPerPage: number | null,
    symbol: string,
    side: string,
    userId?: string,
    asManager?: boolean
  ): Observable<any> {
    const params: any = {
      symbol: symbol,
      side: side,
      page: page,
      itemsPerPage: itemsPerPage,
    };

    if (userId) {
      params.userId = userId;
    }

    if (asManager) {
      params.asManager = true;
    }

    return this.networkHelper.get(`${this.hostUrl}/open-orders`, params);
  }

  deleteOrder(orderId: string): Observable<any> {
    return this.networkHelper.delete(`${this.hostUrl}/orders/${orderId}`, {});
  }

  deleteGtnOrder(orderId: string): Observable<any> {
    return this.networkHelper
      .delete(`${this.hostUrl}/stock/orders/${orderId}`, {})
      .pipe(delay(2000));
  }

  forceCloseGtnOrder(
    orderId: string,
    data: { action: 'cancel' | 'reject'; reason?: string }
  ): Observable<any> {
    const body: any = { action: data.action };

    if (data.reason) {
      body.reason = data.reason;
    }

    return this.networkHelper.post(
      `${this.hostUrl}/admin/stock/orders/${encodeURIComponent(
        orderId
      )}/force-close`,
      body
    );
  }

  deleteOrderBulk(symbol: any = null, userId: null | string = null) {
    const data = {
      symbol: symbol,
      userId: userId,
    };

    return this.networkHelper.delete(`${this.hostUrl}/orders`, data);
  }

  createOrder(data: string): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/orders`, data);
  }

  getGtnOrder(orderId: string): Observable<any> {
    return this.networkHelper.get(`${this.hostUrl}/stock/${orderId}`, {
      gtnOrderId: orderId,
    });
  }

  getGtnOpenOrders(
    userId: string,
    page: number,
    itemsPerPage: number
  ): Observable<any> {
    return this.networkHelper.get(`${this.hostUrl}/open-orders`, {
      userId: userId,
      page: page,
      itemsPerPage: itemsPerPage,
    });
  }

  getGtnOrders(
    userId: string,
    page: number,
    itemsPerPage: number
  ): Observable<any> {
    return this.networkHelper.get(`${this.hostUrl}/stock/orders`, {
      userId: userId,
      page: page,
      itemsPerPage: itemsPerPage,
    });
  }

  /**
   * Narrows the mixed orders history by kind and by currency leg.
   *
   * `orderType` supersedes the older `hideFx` boolean and is the only way to see FX
   * rows on their own — `hideFx` could only ever take them away. Never filter
   * `extra.isFx` in the browser instead: that filters an already-paginated page and
   * silently breaks paging.
   *
   * `currency` matches either leg of the pair, which is how it reaches off-book FX
   * pairs the `symbol` filter cannot — `symbol` only accepts configured tradable
   * pairs. It must be the exact symbol; a lowercase value is a validation error.
   */
  private applyOrderHistoryFilters(
    query: any,
    filters?: OrderHistoryFilters
  ): void {
    if (filters?.orderType && filters.orderType !== 'any') {
      query.orderType = filters.orderType;
    }

    if (filters?.currency && filters.currency !== 'any') {
      query.currency = filters.currency.toUpperCase();
    }
  }

  getOrdersHistory(
    symbol: string,
    side: string,
    type: string,
    page: number,
    itemsPerPage: number,
    startDate: number,
    endDate: number,
    hideCancel: boolean,
    cancelReason: string,
    status: string,
    userId?: string,
    orderID?: string,
    asManager?: boolean,
    filters?: OrderHistoryFilters
  ): Observable<any> {
    const query: any = {
      symbol: symbol,
      side: side,
      type: type,
      page: page,
      itemsPerPage: itemsPerPage,
      startDate: startDate,
      endDate: endDate,
      hideCanceled: hideCancel,
      cancelReason: cancelReason,
      status: status,
    };

    if (userId) {
      query.userId = userId;
    }

    if (orderID) {
      query.orderID = orderID;
    }

    if (asManager) {
      query.asManager = true;
    }

    this.applyOrderHistoryFilters(query, filters);

    return this.networkHelper.get(`${this.hostUrl}/orders/history`, query);
  }

  getOrdersHistoryCsv(
    symbol: string,
    side: string,
    type: string,
    startDate: number,
    endDate: number,
    hideCancel: boolean,
    cancelReason: string,
    userId?: string,
    orderID?: string,
    filters?: OrderHistoryFilters
  ): Observable<any> {
    const tzShift = dayjs().utcOffset();
    const query: any = {
      symbol: symbol,
      side: side,
      type: type,
      startDate: startDate,
      endDate: endDate,
      hideCanceled: hideCancel,
      cancelReason: cancelReason,
      timeZoneShift: tzShift,
    };

    if (userId) {
      query.userId = userId;
    }

    if (orderID) {
      query.orderID = orderID;
    }

    // Accepts the same two filters. The columns are unchanged either way — the
    // generic order columns, with `symbol` holding the pair and `quantity` the spent
    // leg. There are no fromCurrency / toCurrency / rate columns in the export.
    this.applyOrderHistoryFilters(query, filters);

    return this.networkHelper.get(`${this.hostUrl}/orders/csv/history`, query);
  }

  getOtcOrdersHistory(
    page: number,
    itemsPerPage: number,
    startDate: number,
    endDate: number,
    hideCanceled: boolean,
    symbol?: string,
    status?: string,
    side?: string,
    userId?: string,
    cancelReason?: string,
    orderID?: string,
    asManager?: boolean
  ): Observable<any> {
    const query: any = {
      page,
      itemsPerPage,
      startDate,
      endDate,
      hideCanceled,
    };

    if (symbol && symbol !== 'any') query.symbol = symbol;
    if (status && status !== 'any') query.status = status;
    if (side && side !== 'any') query.side = side;
    if (userId && userId !== 'any') query.userId = userId;
    if (cancelReason && cancelReason !== 'any')
      query.cancelReason = cancelReason;

    if (orderID) query.orderID = orderID;
    if (asManager) query.asManager = true;

    return this.networkHelper.get(`${this.hostUrl}/otc-orders/history`, query);
  }

  /**
   * The people behind an order's `statusHistory[].userId`, keyed by id. Loaded when the
   * Status History modal opens — the list endpoints only carry the order owner.
   * FX orders are stock-model orders, so they pass `stock`.
   */
  getStatusHistoryUsers(
    orderID: string,
    kind: 'stock' | 'otc'
  ): Observable<{ users: Record<string, StatusHistoryActor> }> {
    return this.networkHelper.get(`${this.hostUrl}/orders/status-history-users`, {
      orderID,
      kind,
    });
  }

  getOtcOrdersHistoryCsv(
    startDate: number,
    endDate: number,
    hideCanceled: boolean,
    symbol?: string,
    status?: string,
    side?: string,
    userId?: string,
    cancelReason?: string,
    asManager?: boolean
  ): Observable<any> {
    const query: any = {
      startDate,
      endDate,
      hideCanceled,
      timeZoneShift: dayjs().utcOffset(),
    };

    if (symbol && symbol !== 'any') query.symbol = symbol;
    if (status && status !== 'any') query.status = status;
    if (side && side !== 'any') query.side = side;
    if (userId && userId !== 'any') query.userId = userId;
    if (cancelReason && cancelReason !== 'any')
      query.cancelReason = cancelReason;

    if (asManager) query.asManager = true;

    return this.networkHelper.get(
      `${this.hostUrl}/otc-orders/csv/history`,
      query
    );
  }

  estimateGtnOrder(data: {
    symbol: string;
    exchange: string;
    side: 'buy' | 'sell';
    type: 'market' | 'limit';
    quantity: number;
    paymentCurrency: string;
    price?: number;
    withLeverage?: boolean;
    userId?: string;
  }): Observable<{
    estimatedFee: number;
    estimatedTotal: number;
    /** Total + fee on a buy, total − fee on a sell. */
    overallTotal?: number;
    estimatedFrozen: number;
    frozenCurrency: string;
    split?: {
      gtnQuantity: number;
      otcLegs: { custodian: string; quantity: number }[];
    };
  }> {
    return this.networkHelper.post(
      `${this.hostUrl}/stock/orders/estimate`,
      data
    );
  }

  createGtnOrder(data: {
    symbol: string;
    exchange: string;
    side: 'buy' | 'sell';
    type: 'market' | 'limit';
    /** Time in force, limit orders only. Backend default: gtc. */
    subType?: 'gtc' | 'day';
    quantity: number;
    paymentCurrency: string;
    price?: number;
    userId?: string;
    /**
     * Whether the client has to accept the order. The backend defaults it to `true`:
     * the order rests in `pending_confirmation`, unfrozen and unsent, and auto-cancels
     * 5 minutes later if they do not accept.
     *
     * `false` freezes the funds and sends the order to GTN on this call, on terms
     * already agreed off-platform — it comes back `new`, and broker errors surface here.
     */
    requireConfirmation?: boolean;
  }): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/admin/stock/orders`, data);
  }

  estimateOtcOrder(data: {
    tickerId: string;
    sourceId: string;
    paymentCurrency: string;
    /**
     * Price currency per 1 `paymentCurrency` ("1 USD = 7.12 CNY" is 7.12).
     * Required when `paymentCurrency` is not the instrument's price currency —
     * desk callers only.
     */
    paymentRate?: number;
    side: 'buy' | 'sell';
    type: 'market' | 'limit';
    price?: number;
    quantity?: number;
    settlementDate: string;
    userId?: string;
  }): Observable<{
    estimatedFee: number;
    estimatedTotal: number;
    estimatedAci?: number;
    estimatedYield?: number;
    estimatedFrozen: number;
    frozenCurrency: string;
    overallTotal?: number;
    priceCurrency?: string;
    paymentCurrency?: string;
    paymentRate?: number | null;
    /** The same figures in `paymentCurrency`. */
    payment?: OtcEstimatePayment;
  }> {
    return this.networkHelper.post(`${this.hostUrl}/otc-orders/estimate`, data);
  }

  createOtcOrder(data: {
    tickerId: string;
    sourceId: string;
    paymentCurrency: string;
    /**
     * Price currency per 1 `paymentCurrency` ("1 USD = 7.12 CNY" is 7.12).
     * Required when `paymentCurrency` is not the instrument's price currency —
     * desk callers only.
     */
    paymentRate?: number;
    side: 'buy' | 'sell';
    type: 'market' | 'limit';
    price?: number;
    quantity?: number;
    frozenQuantity?: number;
    settlementDate: string;
    userId?: string;
    /**
     * Only read for an order raised on a client's behalf (with `userId`). The backend
     * defaults it to `true`: the order rests in `pending_confirmation` until the client
     * accepts it.
     *
     * `false` raises it straight into `new`, the desk's queue, on terms already agreed
     * off-platform — no confirmation ask. Funds are frozen either way.
     */
    requireConfirmation?: boolean;
  }): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/otc-orders`, data);
  }

  updateOtcOrder(
    orderId: string,
    data: {
      type: 'market' | 'limit';
      frozenQuantity?: number;
      price?: number;
      quantity?: number;
      /** Re-quotes the rate of an order paid in another currency. */
      paymentRate?: number;
      /** YYYY-MM-DD; only honoured for an operator edit of a pending_confirmation order. */
      settlementDate?: string;
    }
  ): Observable<any> {
    return this.networkHelper.put(
      `${this.hostUrl}/otc-orders/${orderId}`,
      data
    );
  }

  markOtcOrderInProgress(orderId: string): Observable<any> {
    return this.networkHelper.put(
      `${this.hostUrl}/otc-orders/${orderId}/in-progress`,
      {}
    );
  }

  finalizeOtcOrder(
    orderId: string,
    data: {
      quantity: number;
      averagePrice: number;
      commission: number;
      settlementDate: string;
      /** Re-quotes the rate of an order paid in another currency. */
      paymentRate?: number;
    }
  ): Observable<any> {
    return this.networkHelper.put(
      `${this.hostUrl}/otc-orders/${orderId}/finalize`,
      data
    );
  }

  /**
   * Sends a finalized order back to the dealer (finalized → in_progress). The
   * optional note is stored in the status history and emailed to the desk.
   */
  returnOtcOrderToDealer(orderId: string, note?: string): Observable<any> {
    return this.networkHelper.put(
      `${this.hostUrl}/otc-orders/${orderId}/return-to-dealer`,
      note ? { note } : {}
    );
  }

  cancelOtcOrder(orderId: string): Observable<any> {
    return this.networkHelper.delete(
      `${this.hostUrl}/otc-orders/${orderId}`,
      {}
    );
  }

  confirmOtcOrderByAdmin(
    orderId: string,
    data: {
      quantity: number;
      averagePrice: number;
      commission: number;
      settlementDate: string;
      /** Re-quotes the rate of an order paid in another currency. */
      paymentRate?: number;
    }
  ): Observable<any> {
    return this.networkHelper.put(
      `${this.hostUrl}/otc-orders/${orderId}/confirm-by-admin`,
      data
    );
  }

  /**
   * confirmed → settled. Applies wallet balances once `settlementDate` has been
   * reached (desk timezone). No body — settles the figures already on the order.
   */
  settleOtcOrderByAdmin(orderId: string): Observable<any> {
    return this.networkHelper.put(
      `${this.hostUrl}/otc-orders/${orderId}/settle`,
      {}
    );
  }
}
